import { geoMercator, geoPath } from "d3-geo";
import { useEffect, useMemo, useState } from "react";
import { feature } from "topojson-client";
import { fmtIDR } from "../lib/format";

type Region = { name: string; total: number; outstanding: number; cols: Record<string, number>; col5: number; score: number };
type Props = { kind: "japan" | "indonesia"; data: Region[]; testId: string };

const JP_NAME = (n: string) => (n === "Hokkai Do" ? "Hokkaido" : n.replace(/ (Ken|Fu|To)$/, ""));

function shade(t: number, dark: boolean) {
  if (t <= 0) return dark ? "hsl(215 20% 22%)" : "hsl(215 20% 90%)";
  const l = 88 - t * 60;
  return `hsl(0 ${55 + t * 35}% ${l}%)`;
}

export function NplMap({ kind, data, testId }: Props) {
  const [geo, setGeo] = useState<any>(null);
  const [hover, setHover] = useState<{ name: string; x: number; y: number } | null>(null);
  useEffect(() => {
    fetch(`/geo/${kind}.json`).then((r) => r.json()).then((j) => {
      setGeo(j.type === "Topology" ? feature(j, j.objects[Object.keys(j.objects)[0]]) : j);
    });
  }, [kind]);
  const byName = useMemo(() => Object.fromEntries(data.map((d) => [d.name.toLowerCase(), d])), [data]);
  const max = Math.max(1, ...data.map((d) => d.score));
  const W = 640, H = kind === "japan" ? 460 : 300;
  const path = useMemo(() => {
    if (!geo) return null;
    const proj = geoMercator().fitExtent([[8, 8], [W - 8, H - 8]], geo);
    return geoPath(proj);
  }, [geo, H]);
  const dark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  const nameOf = (f: any) => (kind === "japan" ? JP_NAME(f.properties.nam) : f.properties.name);
  const hv = hover ? byName[hover.name.toLowerCase()] : null;

  if (!geo || !path) return <div className="h-64 text-sm text-muted-foreground flex items-center justify-center">Memuat peta…</div>;
  return (
    <div className="relative" data-testid={testId}>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        {geo.features.map((f: any, i: number) => {
          const n = nameOf(f);
          const r = byName[n.toLowerCase()];
          const t = r ? Math.max(0.15, r.score / max) : 0;
          return (
            <path
              key={i}
              d={path(f) || ""}
              fill={shade(t, dark)}
              stroke={dark ? "hsl(215 15% 40%)" : "#fff"}
              strokeWidth={0.6}
              className="transition-opacity hover:opacity-80 cursor-pointer"
              data-testid={r ? `${testId}-region-${n.replace(/\s+/g, "-").toLowerCase()}` : undefined}
              onMouseMove={(e) => {
                const b = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
                setHover({ name: n, x: e.clientX - b.left, y: e.clientY - b.top });
              }}
              onMouseLeave={() => setHover(null)}
            />
          );
        })}
      </svg>
      {hover && (
        <div className="pointer-events-none absolute z-20 rounded-md border bg-popover px-3 py-2 text-xs shadow-lg" style={{ left: Math.min(hover.x + 12, 420), top: hover.y + 12 }} data-testid={`${testId}-tooltip`}>
          <p className="font-semibold">{hover.name}</p>
          {hv ? (
            <>
              <p className="text-muted-foreground">{hv.total} pinjaman · {fmtIDR(hv.outstanding)}</p>
              <p className="num mt-1">{[1, 2, 3, 4, 5].map((c) => `C${c}:${hv.cols[String(c)]}`).join("  ")}</p>
            </>
          ) : <p className="text-muted-foreground">Tidak ada pinjaman aktif</p>}
        </div>
      )}
      <div className="absolute bottom-1 right-1 flex items-center gap-1 text-[10px] text-muted-foreground">
        <span>Rendah</span>
        {[0.15, 0.4, 0.65, 0.9, 1].map((t) => <span key={t} className="h-3 w-5 rounded-sm" style={{ background: shade(t, dark) }} />)}
        <span>Col 5 pekat</span>
      </div>
    </div>
  );
}
